import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Car, User, LogOut, Menu, X, FileText, ShoppingBag, FolderOpen, Wrench } from "lucide-react";
import { useState } from "react";
import { logoutCustomer } from "@/Calls/callCustomerAuth";
import { useAuth } from "@/hooks/useAuth";

const navItems = [
    { to: "/customer/dashboard",      label: "Dashboard",   icon: LayoutDashboard },
    { to: "/customer/marketplace",    label: "Browse Cars", icon: ShoppingBag },
    { to: "/customer/vehicles",       label: "My Vehicles", icon: Car },
    { to: "/customer/deals",          label: "My Deals",    icon: FileText },
    { to: "/customer/repair-orders",  label: "My Repairs",  icon: Wrench },
    { to: "/customer/documents",      label: "Documents",   icon: FolderOpen },
    { to: "/customer/profile",        label: "Profile",     icon: User },
];

export default function CustomerLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const { setUser } = useAuth();
    const [mobileOpen, setMobileOpen] = useState(false);

    const handleLogout = async () => {
        try { await logoutCustomer(); } finally {
            setUser(null);
            navigate("/customer/login", { replace: true });
        }
    };

    const NavLinks = () => (
        <>
            {navItems.map(({ to, label, icon: Icon }) => {
                const active = location.pathname === to || location.pathname.startsWith(to + "/");
                return (
                    <Link
                        key={to}
                        to={to}
                        onClick={() => setMobileOpen(false)}
                        className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                            active
                                ? "bg-white/20 text-white font-medium"
                                : "text-white/70 hover:bg-white/10 hover:text-white"
                        }`}
                    >
                        <Icon className="h-4 w-4 shrink-0" />
                        {label}
                    </Link>
                );
            })}
        </>
    );

    return (
        <div className="min-h-screen flex bg-background">
            {/* Desktop sidebar */}
            <aside className="hidden lg:flex w-60 flex-col bg-gradient-to-b from-indigo-700 to-violet-800 text-white p-5 shrink-0">
                <div className="mb-8">
                    <p className="text-xs font-medium uppercase tracking-widest text-white/60 mb-1">IDS CRM</p>
                    <h2 className="text-lg font-semibold">Customer Portal</h2>
                </div>
                <nav className="flex-1 space-y-1">
                    <NavLinks />
                </nav>
                <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/70 hover:bg-white/10 hover:text-white transition-colors mt-4"
                >
                    <LogOut className="h-4 w-4 shrink-0" />
                    Sign Out
                </button>
            </aside>

            {/* Mobile top bar */}
            <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-indigo-700 text-white px-4 py-3 flex items-center justify-between">
                <h2 className="font-semibold text-sm">Customer Portal</h2>
                <button onClick={() => setMobileOpen(!mobileOpen)}>
                    {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            {/* Mobile drawer */}
            {mobileOpen && (
                <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMobileOpen(false)}>
                    <aside
                        className="absolute left-0 top-0 bottom-0 w-60 bg-gradient-to-b from-indigo-700 to-violet-800 text-white p-5"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="mb-8 pt-10">
                            <h2 className="text-lg font-semibold">Customer Portal</h2>
                        </div>
                        <nav className="space-y-1">
                            <NavLinks />
                        </nav>
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/70 hover:bg-white/10 hover:text-white transition-colors mt-4"
                        >
                            <LogOut className="h-4 w-4 shrink-0" />
                            Sign Out
                        </button>
                    </aside>
                </div>
            )}

            {/* Content */}
            <main className="flex-1 min-w-0 p-4 pt-16 lg:p-8 lg:pt-8">
                <Outlet />
            </main>
        </div>
    );
}
